import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Newspaper } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/Button";
import blogData from "../content/blogPosts.json";

const PAGE_SIZE = 3;

const sortedPosts = [...blogData.posts].sort((a, b) => a.order - b.order);

const BlogPage = () => {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(sortedPosts.length / PAGE_SIZE));

  const slice = useMemo(() => {
    const start = page * PAGE_SIZE;
    return sortedPosts.slice(start, start + PAGE_SIZE);
  }, [page]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20 sm:pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden />
            Volver al inicio
          </Link>

          <header className="text-center max-w-3xl mx-auto mb-14 space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-primary/20">
              <Newspaper className="w-4 h-4 text-primary" aria-hidden />
              <span className="text-sm font-medium">Blog GABI</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Datos, barrios y{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">
                decisiones con cabeza
              </span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Guías y análisis sobre compra, inversión y mercado en Sevilla y A Coruña. Sin humo, con fuentes.
            </p>
          </header>

          <div className="grid gap-6 md:gap-8">
            {slice.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group block rounded-3xl border border-border/60 bg-card/40 backdrop-blur-sm p-6 sm:p-8 shadow-lg hover:border-primary/35 hover:shadow-xl transition-all duration-300"
              >
                <article className="space-y-3">
                  <p className="text-xs font-medium uppercase tracking-wider text-primary/90">Mayo 2026 · Equipo GABI</p>
                  <h2 className="text-xl sm:text-2xl font-bold text-foreground group-hover:text-primary transition-colors leading-snug">
                    {post.title}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed line-clamp-3">{post.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
                    Leer artículo
                    <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                  </span>
                </article>
              </Link>
            ))}
          </div>

          {totalPages > 1 ? (
            <nav
              className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
              aria-label="Paginación del blog"
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="gap-1"
              >
                <ChevronLeft className="w-4 h-4" aria-hidden />
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground tabular-nums">
                Página {page + 1} de {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                className="gap-1"
              >
                Siguiente
                <ChevronRight className="w-4 h-4" aria-hidden />
              </Button>
            </nav>
          ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
